import { useState, useEffect } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MarketingLayout from '../components/MarketingLayout';
import { useTranslation } from '../i18n/I18nContext';
import { captureReferralCodeFromUrl } from '../utils/referral-code';

const Auth: FC = () => {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isSessionValid } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Capture ?ref=<code> into localStorage before the user triggers
  // Google OAuth (which redirects through Cognito → Google and loses
  // the query string). See utils/referral-code.ts for the handoff.
  useEffect(() => {
    captureReferralCodeFromUrl();
  }, []);

  useEffect(() => {
    if (isSessionValid) {
      navigate('/articles');
    }
  }, [isSessionValid, navigate]);

  const handleGoogleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      const { signInWithRedirect } = await import('aws-amplify/auth');
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(err instanceof Error ? err.message : t('auth.error_generic'));
      setIsLoading(false);
    }
  };

  return (
    <MarketingLayout>
      <div className="max-w-sm mx-auto px-4 pt-16 pb-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {t('auth.heading')}
          </h1>

          {error && (
            <div className="bg-destructive text-destructive-foreground rounded-md p-4 mb-6">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-3 w-full bg-success text-success-foreground px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {isLoading ? t('auth.redirecting') : t('auth.continue_with_google')}
          </button>

          <div className="flex items-center gap-3">
            <div className="h-px bg-border flex-1" />
            <span className="text-sm text-muted-foreground">{t('auth.or_divider')}</span>
            <div className="h-px bg-border flex-1" />
          </div>

          <button
            onClick={() => navigate('/auth/email')}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-3 w-full border-2 border-success text-success px-8 py-4 rounded-lg text-lg font-semibold hover:bg-success/5 disabled:opacity-50 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="16" x="2" y="4" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            {t('auth.continue_with_email')}
          </button>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default Auth;
